import React, {createElement, useState} from 'react';
import PropTypes from 'prop-types';
import {Row, Grid} from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import {css} from 'aphrodite';

import FontAwesomeIcon from '@fortawesome/react-fontawesome';
import {faChevronCircleUp, faChevronCircleDown, faChevronUp, faChevronDown} from '@fortawesome/fontawesome-pro-regular';

const localStorageBoxedIsiExpanded = localStorage.getItem('boxedIsi-expanded');

const BoxedIsi = ({settings, styleSheet, source, topRightLink, fixed, expandable, enabled}) => {
  const isExpandedInitialState = localStorageBoxedIsiExpanded !== null ? localStorageBoxedIsiExpanded === 'true' : settings.boxedIsi.initializeExpanded;
  const [iconHeight, setIconHeight] = useState(null);
  const [isExpanded, setIsExpanded] = useState(isExpandedInitialState);

  const toggleExpandBox = () => {
    localStorage.setItem('boxedIsi-expanded', !isExpanded);
    setIsExpanded((value) => !value);
  }

  const getChildrenHeight = (node) => {
    if (node && iconHeight === null) {
      setIconHeight(node.offsetHeight);
    }
  }

  const linkRenderer = ({href, children}) => {
    if (settings.content.linkNewTab) {
      return (<a
        rel={'noopener noreferrer'}
        href={href}
        target={'_blank'}
        className={css(styleSheet.isiContentLink)}
      >
        {children}
      </a>);
    }

    return (<a
      href={href}
      className={css(styleSheet.isiContentLink)}
    >
      {children}
    </a>);
  };

  const boldRenderer = ({children}) => {
    return (<span className={css(styleSheet.isiContentBold)}>
      {children}
    </span>);
  };

  const boxedIsiSpecialRenderer = ({level, children}) => {
    return createElement(`h${level}`, {className: css(styleSheet.boxedIsiSpecialRenderer)}, children);
  };

  // vertically align two inline-block elements with one of them having dynamic height
  const topRightLinkHeight = iconHeight ? `${iconHeight}px` : '0';

  const topRightLinkElements = [];
  if (expandable) {
    const expandSymbol = settings.boxedIsi.expandToggleIconCircle ? faChevronCircleUp : faChevronUp;
    const collapseSymbol = settings.boxedIsi.expandToggleIconCircle ? faChevronCircleDown : faChevronDown;

    topRightLinkElements.push(
      <a
        ref={(node) => getChildrenHeight(node)}
        key={'expandLink'}
        data-testid="toggle-expanded-icon"
        style={{display: 'inline-block', lineHeight: topRightLinkHeight}}
        className={'pull-right'}
        onClick={toggleExpandBox}>
        <FontAwesomeIcon
          className={css(styleSheet.topRightExpandToggle)}
          size={settings.boxedIsi.expandToggleIconSize}
          icon={isExpanded ? collapseSymbol : expandSymbol}
        />
      </a>
    );
  }

  if (topRightLink) {
    topRightLinkElements.push(
      <a key={'topLink'} style={{display: 'inline-block', lineHeight: topRightLinkHeight}} className={'pull-right'} href={topRightLink.href}>
        <span className={css(styleSheet.topRightLink)}>
          {topRightLink.name}
        </span>
      </a>
    );
  }

  if (!enabled) {
    return null;
  }

  if (fixed) {
    return (
      <Row data-testid="boxed-isi" className={isExpanded ? css(styleSheet.boxedIsi, styleSheet.fixedIsi, styleSheet.isiExpanded) : css(styleSheet.boxedIsi, styleSheet.fixedIsi, styleSheet.isiShrunk)}>
        <Grid>
          <span>
            {topRightLinkElements}
          </span>
          <ReactMarkdown escapeHtml={false} source={source} renderers={{link: linkRenderer, strong: boldRenderer, heading: boxedIsiSpecialRenderer}} />
        </Grid>
      </Row>
    )
  }

  return (
    <Row data-testid="boxed-isi" className={isExpanded ? css(styleSheet.boxedIsi, styleSheet.isiExpanded) : css(styleSheet.boxedIsi, styleSheet.isiShrunk)}>
      {topRightLinkElements}
      <ReactMarkdown escapeHtml={false} source={source} renderers={{link: linkRenderer, strong: boldRenderer, heading: boxedIsiSpecialRenderer}} />
    </Row>
  );
}

BoxedIsi.protoTypes = {
  settings: PropTypes.object.isRequired,
  styleSheet: PropTypes.object.isRequired,
  source: PropTypes.any.isRequired,
  topRightLink: PropTypes.object.isRequired,
  fixed: PropTypes.object.isRequired,
  expandable: PropTypes.object.isRequired,
  enabled: PropTypes.bool.isRequired,
};

export default BoxedIsi;
